interface LabeledElement {
  guid: string
  label?: string
}

interface PositionLabelSource {
  position_guid: string
  label?: string
}

export const makeSegmentLabel = (segmentNumberInWall: number, wallLabel: string): string =>
  `${wallLabel}.A${segmentNumberInWall.toString().padStart(2, '0')}`

export const buildGuidToLabelFromModel = (model: PlanarModel): Record<string, string> => {
  const labels: Record<string, string> = {}

  const addLabeledElements = (elements: LabeledElement[]) => {
    for (const element of elements) {
      if (element.label) {
        labels[element.guid] = element.label
      }
    }
  }

  addLabeledElements(model.walls)
  addLabeledElements(model.beams)
  addLabeledElements(model.columns)
  addLabeledElements(model.purlins)
  addLabeledElements(model.slabs)
  addLabeledElements(model.roof_slabs)
  addLabeledElements(model.foundationElements)

  for (const slab of model.vertical_slabs) {
    if (slab.label) {
      labels[slab.guid] = slab.label
    }
    if (slab.beam?.label) {
      labels[slab.beam.guid] = slab.beam.label
    }
  }

  for (const roofSlab of model.vertical_roof_slabs) {
    if (roofSlab.label) {
      labels[roofSlab.guid] = roofSlab.label
    }
    if (roofSlab.beam?.label) {
      labels[roofSlab.beam.guid] = roofSlab.beam.label
    }
  }

  const addPositionLabels = (positions: PositionLabelSource[] | undefined) => {
    for (const position of positions ?? []) {
      if (position.label) {
        labels[position.position_guid] = position.label
      }
    }
  }

  addPositionLabels(model.rips)
  addPositionLabels(model.lintels)

  return labels
}

export const buildGuidToLabelFromStiffeningIntervals = (
  stiffeningIntervals: StiffeningSegment[],
  wallGuidToLabel: Record<string, string>,
): Record<string, string> => {
  const labels: Record<string, string> = {}
  const segmentsByWall = new Map<string, StiffeningSegment[]>()

  for (const segment of stiffeningIntervals) {
    if (!segment.guid) {
      continue
    }

    const wallSegments = segmentsByWall.get(segment.element_guid) ?? []
    wallSegments.push(segment)
    segmentsByWall.set(segment.element_guid, wallSegments)
  }

  for (const [wallGuid, segments] of segmentsByWall) {
    const wallLabel = wallGuidToLabel[wallGuid]
    if (!wallLabel) {
      continue
    }

    segments.forEach((segment, index) => {
      if (!segment.guid) {
        return
      }
      labels[segment.guid] = makeSegmentLabel(index + 1, wallLabel)
    })
  }

  return labels
}

export const buildGuidToLabelMap = (
  model: PlanarModel,
  stiffeningIntervals: StiffeningSegment[] = [],
): Record<string, string> => {
  const labels = buildGuidToLabelFromModel(model)

  return {
    ...labels,
    ...buildGuidToLabelFromStiffeningIntervals(stiffeningIntervals, labels),
  }
}
